import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

/**
 * Editable text component.
 * - Renders `fallback` text if no override in DB.
 * - Shows edit affordance when editMode is on.
 * - Clicking edit turns it into an inline editor.
 *
 * Props:
 *   contentKey: string (unique key like "home.hero.title")
 *   fallback: default text if no override
 *   as: HTML tag or component to render (default: 'span')
 *   multiline: boolean, use textarea (default: false)
 *   className: passed to underlying element
 */
export default function Editable({ contentKey, fallback, as: Tag = 'span', multiline = false, className = '', ...rest }) {
  const { editMode, getText, updateText } = useSiteContent();
  const value = getText(contentKey, fallback);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const startEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraft(value);
    setError('');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError('');
    setDraft(value);
  };

  const save = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    setError('');
    try {
      await updateText(contentKey, draft);
      setEditing(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') cancel();
    if (!multiline && e.key === 'Enter') { e.preventDefault(); save(); }
    if (multiline && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  };

  if (editing) {
    const InputTag = multiline ? 'textarea' : 'input';
    return (
      <span className="relative inline-flex flex-col gap-1" data-testid={`editable-editing-${contentKey}`}>
        <InputTag
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={multiline ? 3 : undefined}
          className={`bg-[#0A0A0A] border-2 border-[#0A84FF] rounded-lg px-3 py-1.5 text-white outline-none min-w-[200px] ${multiline ? 'w-full min-w-[300px]' : ''} ${className}`}
        />
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid={`editable-save-${contentKey}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#32D74B] text-black text-xs font-medium hover:bg-[#28C042] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" strokeWidth={3} />}
            Save
          </button>
          <button
            type="button"
            onClick={cancel}
            data-testid={`editable-cancel-${contentKey}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-medium hover:bg-white/20"
          >
            <X className="w-3 h-3" strokeWidth={3} />
            Cancel
          </button>
          {error && <span className="text-xs text-[#FF453A]">{error}</span>}
        </div>
      </span>
    );
  }

  if (editMode) {
    return (
      <Tag className={`${className} relative inline group cursor-pointer`} onClick={startEdit} data-testid={`editable-${contentKey}`} {...rest}>
        <motion.span
          whileHover={{ backgroundColor: 'rgba(10, 132, 255, 0.15)' }}
          className="relative rounded-md px-1 -mx-1 outline-dashed outline-2 outline-[#0A84FF]/50 outline-offset-2"
        >
          {value}
        </motion.span>
        <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#0A84FF] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <Pencil className="w-3 h-3" strokeWidth={2} />
        </span>
      </Tag>
    );
  }

  return <Tag className={className} data-testid={`editable-${contentKey}`} {...rest}>{value}</Tag>;
}
